// @ts-nocheck
import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Plus,
  Download,
  Image as ImageIcon,
  Trash2,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
  ZoomIn,
  ZoomOut,
  ArrowRight,
  ArrowDown,
  PanelRightClose,
  PanelRightOpen,
  Globe,
  Link as LinkIcon,
  ExternalLink,
  Hash,
  Hexagon,
  Pencil,
  Focus,
} from "lucide-react";

// --- Configurazione Tipologie ---
const NODE_TYPES = {
  home: { id: "home", label: "Home", color: "#10b981" },
  file: { id: "file", label: "File", color: "#da2778" },
  page: { id: "page", label: "Pagina", color: "#3b82f6" },
  func: { id: "func", label: "Funzionalità", color: "#a855f7" },
  popup: { id: "popup", label: "Popup", color: "#f97316" },
  private: { id: "private", label: "Area Riservata", color: "#e11d48" },
  legal: { id: "legal", label: "Script / API", color: "#5d4039" },
};

const NODE_WIDTH = 280;
const SPACING_X = 340;
const MIN_SPACING_Y = 100;

const BASE_OFFSET_X = 600;
const BASE_OFFSET_Y = 600;

const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export default function App() {
  const [projectName, setProjectName] = useState("Nuovo Progetto");
  const [nodes, setNodes] = useState([
    {
      id: "1",
      type: "home",
      parentId: null,
      isExpanded: true,
      label: "Home",
      urls: [{ id: "u1", lang: "IT", url: "/" }],
      internalLinks: [{ id: "l1", label: "Main Navigation" }],
      description: "Pagina di atterraggio principale.",
    },
  ]);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("vertical");
  const [zoom, setZoom] = useState(0.8);
  const [panOffset, setPanOffset] = useState({ x: -100, y: -150 });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [theme, setTheme] = useState("dark");
  const [isExporting, setIsExporting] = useState(false);
  const [renderedHeights, setRenderedHeights] = useState({});

  const nodeRefs = useRef({});
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let changed = false;
    const newHeights = { ...renderedHeights };
    nodes.forEach((n) => {
      const el = nodeRefs.current[n.id];
      if (el) {
        const h = el.offsetHeight;
        if (h && Math.abs((newHeights[n.id] || 0) - h) > 2) {
          newHeights[n.id] = h;
          changed = true;
        }
      }
    });
    if (changed) setRenderedHeights(newHeights);
  });

  const getFormattedDate = () =>
    new Intl.DateTimeFormat("it-IT").format(new Date());

  const layoutNodes = useMemo(() => {
    const tree = {};
    nodes.forEach((n) => {
      if (!tree[n.parentId]) tree[n.parentId] = [];
      tree[n.parentId].push(n);
    });

    const positions = {};
    const nodeHeights = {};
    nodes.forEach((n) => {
      let est = 65;
      if (n.isExpanded) {
        est += 16;
        est += (n.urls?.length || 0) * 36;
        if (n.internalLinks?.length > 0)
          est += 24 + n.internalLinks.length * 30;
        if (n.description) est += 45;
      }
      nodeHeights[n.id] = renderedHeights[n.id] || est;
    });

    const calculate = (id, level, offset) => {
      const children = tree[id] || [];
      const node = nodes.find((n) => n.id === id);
      if (!node) return offset;
      let currentPos = offset;
      children.forEach((child) => {
        currentPos = calculate(child.id, level + 1, currentPos);
      });
      const myBreadth =
        children.length > 0
          ? (positions[children[0].id].breadth +
              positions[children[children.length - 1].id].breadth) /
            2
          : offset;
      positions[id] = {
        ...node,
        depth: level,
        breadth: myBreadth,
        height: nodeHeights[id],
      };
      return children.length > 0 ? currentPos : offset + 1;
    };

    if (nodes.length > 0) calculate(nodes[0].id, 0, 0);
    const finalNodes = Object.values(positions);
    const getVerticalY = (node) => {
      if (!node.parentId) return 0;
      const parent = positions[node.parentId];
      if (!parent) return 0;
      return getVerticalY(parent) + parent.height + MIN_SPACING_Y;
    };

    return finalNodes.map((p) => ({
      ...p,
      x:
        (viewMode === "vertical"
          ? p.breadth * SPACING_X
          : p.depth * SPACING_X) + BASE_OFFSET_X,
      y:
        (viewMode === "vertical"
          ? getVerticalY(p)
          : p.breadth * (NODE_WIDTH + 100)) + BASE_OFFSET_Y,
    }));
  }, [nodes, viewMode, renderedHeights]);

  const edges = useMemo(
    () =>
      nodes
        .filter((n) => n.parentId !== null)
        .map((n) => ({
          id: `e-${n.parentId}-${n.id}`,
          source: n.parentId,
          target: n.id,
        })),
    [nodes]
  );

  const handleWheel = (e) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const pointerX = e.clientX - containerRect.left;
    const pointerY = e.clientY - containerRect.top;
    const scaleMultiplier = e.deltaY > 0 ? 0.99 : 1.01;
    setZoom((prevZoom) => {
      const newZoom = Math.min(Math.max(prevZoom * scaleMultiplier, 0.1), 2);
      setPanOffset((prevPan) => {
        const targetX = (pointerX - prevPan.x) / prevZoom;
        const targetY = (pointerY - prevPan.y) / prevZoom;
        return {
          x: pointerX - targetX * newZoom,
          y: pointerY - targetY * newZoom,
        };
      });
      return newZoom;
    });
  };

  const handleZoomCenter = (direction) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    setZoom((prevZoom) => {
      const multiplier = direction === "in" ? 1.15 : 0.85;
      const newZoom = Math.min(Math.max(prevZoom * multiplier, 0.1), 2);
      setPanOffset((prevPan) => {
        const targetX = (centerX - prevPan.x) / prevZoom;
        const targetY = (centerY - prevPan.y) / prevZoom;
        return {
          x: centerX - targetX * newZoom,
          y: centerY - targetY * newZoom,
        };
      });
      return newZoom;
    });
  };

  const handleCenterView = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rootNode = layoutNodes.find((n) => n.parentId === null);
    if (!rootNode) return;
    const targetZoom = 1;
    let targetX = rootNode.x + NODE_WIDTH / 2;
    let targetY = rootNode.y + (renderedHeights[rootNode.id] || 65) / 2;
    if (viewMode === "vertical") targetY -= 60;
    else targetX -= 150;
    setZoom(targetZoom);
    setPanOffset({
      x: centerX - targetX * targetZoom,
      y: centerY - targetY * targetZoom,
    });
  };

  const handleCanvasMouseDown = (e) => {
    if (e.button === 1 || e.target.id === "canvas-bg") {
      const startX = e.clientX - panOffset.x;
      const startY = e.clientY - panOffset.y;
      const onMouseMove = (mE) =>
        setPanOffset({ x: mE.clientX - startX, y: mE.clientY - startY });
      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
  };

  const addNode = (targetId, type = "child") => {
    const targetNode = nodes.find((n) => n.id === targetId);
    if (!targetNode) return;
    const newNodeId = Date.now().toString();
    let newParentId = type === "child" ? targetId : targetNode.parentId;
    const newNode = {
      id: newNodeId,
      type: "page",
      parentId: newParentId,
      label: "Nuova Pagina",
      isExpanded: true,
      urls: [{ id: Date.now().toString() + "u", lang: "IT", url: "" }],
      internalLinks: [],
      description: "",
    };
    
    setNodes((prev) => {
      if (type === "parent") {
        const targetIndex = prev.findIndex((n) => n.id === targetId);
        const newNodes = [...prev];
        newNodes.splice(targetIndex, 0, newNode);
        newNodes[targetIndex + 1] = {
          ...newNodes[targetIndex + 1],
          parentId: newNodeId,
        };
        return newNodes;
      } else if (type === "sibling") {
        const targetIndex = prev.findIndex((n) => n.id === targetId);
        const newNodes = [...prev];
        newNodes.splice(targetIndex + 1, 0, newNode); // Aggiunge DOPO
        return newNodes;
      } else if (type === "sibling-before") {
        const targetIndex = prev.findIndex((n) => n.id === targetId);
        const newNodes = [...prev];
        newNodes.splice(targetIndex, 0, newNode); // Aggiunge PRIMA
        return newNodes;
      } else return [...prev, newNode];
    });
    setSelectedId(newNodeId);
  };

  const updateNode = (id, updates) =>
    setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, ...updates } : n)));

  const deleteNode = (id) => {
    if (nodes.find((n) => n.id === id)?.type === "home") return;
    const toDelete = [id];
    const findChildren = (pid) =>
      nodes
        .filter((n) => n.parentId === pid)
        .forEach((c) => {
          toDelete.push(c.id);
          findChildren(c.id);
        });
    findChildren(id);
    setNodes((nds) => nds.filter((n) => !toDelete.includes(n.id)));
    setSelectedId(null);
  };

  const handleExport = async (format) => {
    setIsExporting(format);
    setSelectedId(null);
    try {
      await loadScript(
        "https://cdnjs.cloudflare.com/ajax/libs/html-to-image/1.11.11/html-to-image.min.js"
      );
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      // Calcolo area nodi
      layoutNodes.forEach((n) => {
        minX = Math.min(minX, n.x);
        minY = Math.min(minY, n.y);
        maxX = Math.max(maxX, n.x + NODE_WIDTH);
        maxY = Math.max(maxY, n.y + (renderedHeights[n.id] || 200));
      });

      // Calcolo area Card Progetto
      const rootNode = layoutNodes[0];
      if (rootNode) {
        const projX = viewMode === "vertical" ? rootNode.x : rootNode.x - 300;
        const projY = viewMode === "vertical" ? rootNode.y - 120 : rootNode.y;
        minX = Math.min(minX, projX);
        minY = Math.min(minY, projY);
        maxX = Math.max(maxX, projX + 280);
        maxY = Math.max(maxY, projY + 70);
      }

      const padding = 160;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;
      const exportWidth = maxX - minX;
      const exportHeight = maxY - minY;

      let renderScale =
        exportWidth * 2 > 14000 || exportHeight * 2 > 14000 ? 1 : 2;

      const targetEl = canvasRef.current;
      const originalTransform = targetEl.style.transform;
      const originalWidth = targetEl.style.width;
      const originalHeight = targetEl.style.height;

      targetEl.style.transform = "none";
      targetEl.style.width = `${maxX}px`;
      targetEl.style.height = `${maxY}px`;
      await new Promise((r) => setTimeout(r, 250));

      const imageOptions = {
        backgroundColor: theme === "dark" ? "#0a0a0a" : "#ffffff",
        pixelRatio: renderScale,
        quality: 0.85,
      };

      const dataUrl =
        format === "png"
          ? await window.htmlToImage.toPng(targetEl, imageOptions)
          : await window.htmlToImage.toJpeg(targetEl, imageOptions);

      targetEl.style.transform = originalTransform;
      targetEl.style.width = originalWidth;
      targetEl.style.height = originalHeight;

      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = exportWidth * renderScale;
      cropCanvas.height = exportHeight * renderScale;
      const ctx = cropCanvas.getContext("2d");
      const img = new Image();
      img.src = dataUrl;
      await new Promise((r) => (img.onload = r));
      ctx.drawImage(
        img,
        minX * renderScale,
        minY * renderScale,
        exportWidth * renderScale,
        exportHeight * renderScale,
        0,
        0,
        exportWidth * renderScale,
        exportHeight * renderScale
      );

      const finalDataUrl = cropCanvas.toDataURL(
        format === "png" ? "image/png" : "image/jpeg",
        0.85
      );
      const fileName = `${projectName.replace(
        /\s+/g,
        "_"
      )}_${getFormattedDate().replace(/\//g, "-")}`;

      if (format === "png") {
        const link = document.createElement("a");
        link.download = `${fileName}.png`;
        link.href = finalDataUrl;
        link.click();
      } else {
        await loadScript(
          "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"
        );
        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF({
          orientation: exportWidth > exportHeight ? "landscape" : "portrait",
          unit: "px",
          format: [exportWidth, exportHeight],
        });
        pdf.addImage(
          finalDataUrl,
          "JPEG",
          0,
          0,
          exportWidth,
          exportHeight,
          undefined,
          "FAST"
        );
        pdf.save(`${fileName}.pdf`);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsExporting(false);
    }
  };

  const styles = `
    .hive-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
    .hive-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .hive-scrollbar::-webkit-scrollbar-thumb { background: rgba(155, 155, 155, 0.3); border-radius: 10px; }
    .sitemap-wrapper[data-theme="dark"] { --bg: #0a0a0a; --card: #1a1a1a; --border: #333; --text: #f0f0f0; --text-dim: #999; --header-bg: #111; --sel-border: #ffffff; }
    .sitemap-wrapper[data-theme="light"] { --bg: #f5f5f7; --card: #ffffff; --border: #e0e0e0; --text: #1a1a1a; --text-dim: #666; --header-bg: #ffffff; --sel-border: #000000; }
    .node-card { background: var(--card); color: var(--text); border: 1px solid var(--border); border-top-width: 4px; border-radius: 12px; width: ${NODE_WIDTH}px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); cursor: default; }
    .node-card.selected { border-color: var(--sel-border); box-shadow: 0 0 0 3px rgba(120, 120, 120, 0.2); z-index: 50; }
    .node-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 3px 8px; border-radius: 4px; color: white; position: absolute; top: -12px; right: 12px; }
    .add-btn { position: absolute; width: 22px; height: 22px; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transition: all 0.2s; cursor: pointer; z-index: 60; border: 2px solid var(--card); }
    .node-card:hover .add-btn { opacity: 1; }
    .add-btn:hover { transform: scale(1.2); z-index: 70; }
    .grab-cursor { cursor: grab !important; }
    .grab-cursor:active { cursor: grabbing !important; }
    .ttip { position: absolute; background: var(--card); color: var(--text); border: 1px solid var(--border); padding: 4px 8px; border-radius: 6px; font-family: monospace; font-size: 10px; white-space: nowrap; opacity: 0; pointer-events: none; transition: all 0.2s; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 100; }
    .group:hover > .ttip { opacity: 1; }
    .ttip-b { top: 100%; left: 50%; transform: translateX(-50%) translateY(8px); }
    .ttip-t { bottom: 100%; left: 50%; transform: translateX(-50%) translateY(-8px); }
    .ttip-l { right: 100%; top: 50%; transform: translateY(-50%) translateX(-8px); }
    .export-active .hide-on-export { display: none !important; }
    .btn-delete { background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.3); color: #dc2626; }
    .sitemap-wrapper[data-theme="dark"] .btn-delete { background: rgba(239, 68, 68, 0.15); border-color: rgba(239, 68, 68, 0.4); color: #f87171; }
    .btn-delete:hover:not(:disabled) { background: #ef4444 !important; color: white !important; }
    .btn-delete:disabled { opacity: 0.4; grayscale: 1; cursor: not-allowed; }
    .label-mini { font-size: 8px; font-weight: 800; text-transform: uppercase; color: var(--text-dim); margin-bottom: 4px; display: block; letter-spacing: 0.05em; }
  `;

  const currentNode = nodes.find((n) => n.id === selectedId);

  return (
    <div
      className={`sitemap-wrapper flex flex-col h-screen w-full font-sans overflow-hidden ${
        isExporting ? "export-active" : ""
      }`}
      data-theme={theme}
    >
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      {isExporting && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[var(--card)] p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-5 border border-[var(--border)]">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-[var(--text)] font-bold">
              Generazione {isExporting.toUpperCase()}...
            </span>
          </div>
        </div>
      )}

      <header className="h-20 flex-none border-b border-[var(--border)] bg-[var(--header-bg)] flex items-center justify-between px-6 z-30 shadow-md">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2 text-blue-600">
            <Hexagon className="w-8 h-8" strokeWidth={2} />
            <span className="font-bold text-xl tracking-tight text-[var(--text)]">
              Hivemap
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[var(--bg)] rounded-lg p-1 border border-[var(--border)]">
            <div className="group relative">
              <button
                onClick={handleCenterView}
                className="p-1.5 hover:bg-[var(--card)] rounded text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
              >
                <Focus size={16} />
              </button>
              <span className="ttip ttip-b hide-on-export">centra:home</span>
            </div>
          </div>
          <div className="flex items-center bg-[var(--bg)] rounded-lg p-1 border border-[var(--border)] mr-1">
            <div className="group relative">
              <button
                onClick={() => handleZoomCenter("out")}
                className="p-1.5 hover:bg-[var(--card)] rounded text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
              >
                <ZoomOut size={16} />
              </button>
              <span className="ttip ttip-b hide-on-export">zoom:riduci</span>
            </div>
            <span className="text-[10px] font-bold w-10 text-center text-[var(--text)] select-none cursor-default">
              {Math.round(zoom * 100)}%
            </span>
            <div className="group relative">
              <button
                onClick={() => handleZoomCenter("in")}
                className="p-1.5 hover:bg-[var(--card)] rounded text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
              >
                <ZoomIn size={16} />
              </button>
              <span className="ttip ttip-b hide-on-export">zoom:aumenta</span>
            </div>
          </div>
          <div className="flex bg-[var(--bg)] rounded-lg p-1 border border-[var(--border)] mr-1">
            <button
              onClick={() => setViewMode("vertical")}
              className={`p-1.5 rounded transition-all ${
                viewMode === "vertical"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-[var(--text-dim)] hover:text-[var(--text)]"
              }`}
            >
              <ArrowDown size={16} />
            </button>
            <button
              onClick={() => setViewMode("horizontal")}
              className={`p-1.5 rounded transition-all ${
                viewMode === "horizontal"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-[var(--text-dim)] hover:text-[var(--text)]"
              }`}
            >
              <ArrowRight size={16} />
            </button>
          </div>
          <div
            className="group relative flex bg-[var(--bg)] rounded-lg p-1 border border-[var(--border)] cursor-pointer mr-1"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          >
            <div
              className={`p-1.5 rounded transition-colors ${
                theme === "dark"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-[var(--text-dim)]"
              }`}
            >
              <Moon size={16} />
            </div>
            <div
              className={`p-1.5 rounded transition-colors ${
                theme === "light"
                  ? "bg-yellow-500 text-white shadow-sm"
                  : "text-[var(--text-dim)]"
              }`}
            >
              <Sun size={16} />
            </div>
            <span className="ttip ttip-b hide-on-export">tema:cambia</span>
          </div>
          <div className="group relative ml-2">
            <button
              onClick={() => handleExport("png")}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-lg shadow-blue-600/20"
            >
              <ImageIcon size={14} /> PNG
            </button>
            <span className="ttip ttip-b hide-on-export">esporta:immagine</span>
          </div>
          <div className="group relative ml-1">
            <button
              onClick={() => handleExport("pdf")}
              className="flex items-center gap-2 bg-[var(--text)] text-[var(--bg)] px-4 py-2 rounded-lg text-xs font-bold shadow-md"
            >
              <Download size={14} /> PDF
            </button>
            <span className="ttip ttip-b hide-on-export">
              esporta:documento
            </span>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden bg-[var(--bg)] grab-cursor"
          onWheel={handleWheel}
          onMouseDown={handleCanvasMouseDown}
        >
          <div
            id="canvas-bg"
            ref={canvasRef}
            className="absolute inset-0 origin-top-left"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              width: "12000px",
              height: "12000px",
            }}
          >
            <svg className="absolute inset-0 pointer-events-none w-full h-full">
              {edges.map((edge) => {
                const src = layoutNodes.find((n) => n.id === edge.source),
                  tgt = layoutNodes.find((n) => n.id === edge.target);
                if (!src || !tgt) return null;
                const d =
                  viewMode === "vertical"
                    ? `M ${src.x + 140} ${src.y + src.height - 1} C ${
                        src.x + 140
                      } ${src.y + src.height + 50}, ${tgt.x + 140} ${
                        tgt.y - 50
                      }, ${tgt.x + 140} ${tgt.y}`
                    : `M ${src.x + 280} ${src.y + 35} C ${src.x + 330} ${
                        src.y + 35
                      }, ${tgt.x - 50} ${tgt.y + 35}, ${tgt.x} ${tgt.y + 35}`;
                return (
                  <path
                    key={edge.id}
                    d={d}
                    stroke={theme === "dark" ? "#71717a" : "#9ca3af"}
                    strokeWidth="1.5"
                    fill="none"
                  />
                );
              })}
            </svg>

            <div
              className="absolute p-4 border-2 border-solid border-blue-500/40 bg-blue-500/5 flex flex-col justify-center transition-all z-10 group"
              style={{
                width: 280,
                height: 70,
                left:
                  viewMode === "vertical"
                    ? layoutNodes[0].x
                    : layoutNodes[0].x - 300,
                top:
                  viewMode === "vertical"
                    ? layoutNodes[0].y - 120
                    : layoutNodes[0].y,
              }}
            >
              <div className="relative flex items-center justify-center w-full">
                {isExporting ? (
                  <div className="text-base font-bold text-[var(--text)]">
                    {projectName}
                  </div>
                ) : (
                  <input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="bg-transparent border-none outline-none text-base font-medium text-[var(--text)] w-full text-center"
                  />
                )}
                <Pencil
                  size={14}
                  className="hide-on-export absolute right-1 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <span className="text-[9px] text-[var(--text-dim)] font-semibold text-center mt-1 uppercase tracking-widest">
                {getFormattedDate()}
              </span>
            </div>

            {layoutNodes.map((node) => (
              <div
                key={node.id}
                ref={(el) => (nodeRefs.current[node.id] = el)}
                className={`node-card absolute p-4 flex flex-col ${
                  selectedId === node.id ? "selected" : ""
                }`}
                style={{
                  left: node.x,
                  top: node.y,
                  borderTopColor: NODE_TYPES[node.type].color,
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedId(node.id);
                }}
              >
                <div
                  className="node-badge"
                  style={{ background: NODE_TYPES[node.type].color }}
                >
                  {NODE_TYPES[node.type].label}
                </div>
                <div className="flex items-center justify-between gap-3 mt-1 group relative">
                  <h3
                    className="font-semibold text-sm text-[var(--text)] truncate flex-1 cursor-pointer hover:text-blue-500 transition-colors"
                    onClick={() =>
                      updateNode(node.id, { isExpanded: !node.isExpanded })
                    }
                  >
                    {node.label}
                  </h3>
                  <button
                    onClick={() =>
                      updateNode(node.id, { isExpanded: !node.isExpanded })
                    }
                    className="hide-on-export text-[var(--text-dim)] transition-colors"
                  >
                    {node.isExpanded ? (
                      <ChevronUp size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </button>
                  <span className="ttip ttip-b hide-on-export">
                    azione:espandi_riduci
                  </span>
                </div>
                {node.isExpanded && (
                  <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-3">
                    {node.urls &&
                      node.urls.length > 0 &&
                      node.urls.some((u) => u.url) && (
                        <div className="space-y-1.5">
                          {node.urls.map(
                            (u) =>
                              u.url && (
                                <div
                                  key={u.id}
                                  className="flex items-center gap-2 text-[10px] text-[var(--text-dim)] truncate"
                                >
                                  <span className="text-blue-500 font-bold bg-blue-500/10 px-1.5 py-0.5 rounded">
                                    {u.lang}
                                  </span>
                                  {u.url}
                                </div>
                              )
                          )}
                        </div>
                      )}
                    {node.internalLinks && node.internalLinks.length > 0 && (
                      <div className="space-y-1.5">
                        <span className="label-mini">Link Interni</span>
                        {node.internalLinks.map((l) => (
                          <div
                            key={l.id}
                            className="flex items-center gap-2 text-[10px] text-emerald-500 font-bold bg-emerald-500/5 border border-emerald-500/20 px-2 py-1 rounded-md"
                          >
                            <Hash size={12} />
                            {l.label}
                          </div>
                        ))}
                      </div>
                    )}
                    {node.description && (
                      <p className="text-[11px] text-[var(--text-dim)] italic leading-relaxed">
                        {node.description}
                      </p>
                    )}
                  </div>
                )}
                {/* Bottone Figlio (Blu) */}
                <div
                  className="add-btn hide-on-export bg-blue-500 group"
                  style={
                    viewMode === "vertical"
                      ? { bottom: -11, left: 129 }
                      : { right: -11, top: 24 }
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    addNode(node.id, "child");
                  }}
                >
                  <Plus size={14} />
                  <span className="ttip ttip-b">nodo:aggiungi_sotto</span>
                </div>
                
                {/* Bottone Fratello Dopo (Smeraldo) */}
                {node.type !== "home" && (
                  <div
                    className="add-btn hide-on-export bg-emerald-500 group"
                    style={
                      viewMode === "vertical"
                        ? { right: -11, top: 24 }
                        : { bottom: -11, left: 129 }
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      addNode(node.id, "sibling");
                    }}
                  >
                    <Plus size={14} />
                    <span className="ttip ttip-b">nodo:aggiungi_dopo</span>
                  </div>
                )}

                {/* NUOVO: Bottone Fratello Prima (Smeraldo speculare) */}
                {node.type !== "home" && (
                  <div
                    className="add-btn hide-on-export bg-emerald-500 group"
                    style={
                      viewMode === "vertical"
                        ? { left: -11, top: 24 }
                        : { top: -11, left: 129 }
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      addNode(node.id, "sibling-before");
                    }}
                  >
                    <Plus size={14} />
                    <span className="ttip ttip-t">nodo:aggiungi_prima</span>
                  </div>
                )}

                {/* Bottone Genitore (Viola) */}
                {node.type !== "home" && (
                  <div
                    className="add-btn hide-on-export bg-purple-500 group"
                    style={
                      viewMode === "vertical"
                        ? { top: -11, left: 129 }
                        : { left: -11, top: 24 }
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      addNode(node.id, "parent");
                    }}
                  >
                    <Plus size={14} />
                    <span className="ttip ttip-t">nodo:aggiungi_sopra</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <aside
          className={`${
            isSidePanelOpen ? "w-[360px]" : "w-0"
          } transition-all duration-300 bg-[var(--card)] border-l border-[var(--border)] flex flex-col z-40 overflow-hidden shadow-2xl`}
        >
          <div className="p-5 border-b border-[var(--border)] flex items-center justify-between bg-[var(--header-bg)]">
            <h2 className="font-bold text-xs uppercase text-[var(--text-dim)] tracking-widest flex items-center gap-2">
              <PanelRightOpen size={14} /> Proprietà
            </h2>
            <button
              onClick={() => setIsSidePanelOpen(false)}
              className="text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
            >
              <PanelRightClose size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 hive-scrollbar space-y-8">
            {!currentNode ? (
              <div className="py-24 text-center text-[11px] text-[var(--text-dim)] italic px-10">
                Seleziona una card sulla mappa per visualizzare e modificare le
                sue proprietà.
              </div>
            ) : (
              <>
                <section>
                  <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase block mb-4 tracking-wider">
                    Tipologia di Contenuto
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(NODE_TYPES).map(([k, v]) => {
                      const isHome = currentNode.type === "home";
                      const isCurrent = currentNode.type === k;
                      if (!isHome && k === "home") return null;
                      if (isHome && k === "file") return null;

                      return (
                        <button
                          key={k}
                          onClick={() => updateNode(selectedId, { type: k })}
                          disabled={isHome}
                          className={`p-2.5 rounded-lg border text-[11px] font-bold transition-all flex items-center gap-2 
                            ${
                              isCurrent
                                ? "bg-[var(--header-bg)] border-blue-500 text-[var(--text)]"
                                : "border-[var(--border)] text-[var(--text-dim)] opacity-80 hover:opacity-100 hover:text-[var(--text)]"
                            } 
                            ${
                              isHome && !isCurrent
                                ? "grayscale opacity-40 cursor-not-allowed"
                                : ""
                            }`}
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: v.color }}
                          ></span>
                          {v.label}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="space-y-5 pt-4 border-t border-[var(--border)]">
                  <div>
                    <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase block mb-2 tracking-wider">
                      Titolo della Card
                    </label>
                    <input
                      className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg p-3 text-sm text-[var(--text)] outline-none focus:border-blue-500 transition-colors font-medium shadow-inner"
                      value={currentNode.label || ""}
                      onChange={(e) =>
                        updateNode(selectedId, { label: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase block mb-2 tracking-wider">
                      Indirizzi URL (Multilingua)
                    </label>
                    <div className="space-y-2">
                      {(currentNode.urls || []).map((u, idx) => (
                        <div key={u.id} className="flex gap-2">
                          <input
                            className="w-14 bg-[var(--bg)] border border-[var(--border)] rounded-lg p-2 text-[10px] font-bold text-center text-blue-500"
                            value={u.lang}
                            onChange={(e) => {
                              const newUrls = [...currentNode.urls];
                              newUrls[idx].lang = e.target.value.toUpperCase();
                              updateNode(selectedId, { urls: newUrls });
                            }}
                          />
                          <input
                            className="flex-1 bg-[var(--bg)] border border-[var(--border)] rounded-lg p-2 text-xs text-[var(--text)]"
                            value={u.url}
                            onChange={(e) => {
                              const newUrls = [...currentNode.urls];
                              newUrls[idx].url = e.target.value;
                              updateNode(selectedId, { urls: newUrls });
                            }}
                          />
                          <button
                            onClick={() => {
                              const newUrls = currentNode.urls.filter(
                                (_, i) => i !== idx
                              );
                              updateNode(selectedId, { urls: newUrls });
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newUrls = [
                            ...(currentNode.urls || []),
                            { id: Date.now().toString(), lang: "EN", url: "" },
                          ];
                          updateNode(selectedId, { urls: newUrls });
                        }}
                        className="w-full py-2 border border-dashed border-[var(--border)] rounded-lg text-[10px] font-bold text-[var(--text-dim)] hover:text-blue-500 transition-colors uppercase"
                      >
                        + Aggiungi Lingua
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase block mb-2 tracking-wider">
                      Link Interni (Collegamenti)
                    </label>
                    <div className="space-y-2">
                      {(currentNode.internalLinks || []).map((link, idx) => (
                        <div key={link.id} className="flex gap-2">
                          <div className="flex-1 relative">
                            <Hash
                              className="absolute left-3 top-2.5 text-emerald-500"
                              size={12}
                            />
                            <input
                              className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg p-2 pl-8 text-xs text-[var(--text)] outline-none focus:border-emerald-500"
                              value={link.label}
                              onChange={(e) => {
                                const newLinks = [...currentNode.internalLinks];
                                newLinks[idx].label = e.target.value;
                                updateNode(selectedId, {
                                  internalLinks: newLinks,
                                });
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              const newLinks = currentNode.internalLinks.filter(
                                (_, i) => i !== idx
                              );
                              updateNode(selectedId, {
                                internalLinks: newLinks,
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newLinks = [
                            ...(currentNode.internalLinks || []),
                            { id: Date.now().toString(), label: "" },
                          ];
                          updateNode(selectedId, { internalLinks: newLinks });
                        }}
                        className="w-full py-2 border border-dashed border-emerald-500/40 rounded-lg text-[10px] font-bold text-emerald-500 hover:bg-emerald-500/10 transition-colors uppercase"
                      >
                        + Aggiungi Link Interno
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase block mb-2 tracking-wider">
                      Note / Descrizione
                    </label>
                    <textarea
                      className="w-full bg-[var(--bg)] border border-[var(--border)] rounded-lg p-3 text-xs text-[var(--text)] min-h-[100px] outline-none focus:border-blue-500 leading-relaxed shadow-inner"
                      placeholder="Descrivi la funzione di questa pagina..."
                      value={currentNode.description || ""}
                      onChange={(e) =>
                        updateNode(selectedId, { description: e.target.value })
                      }
                    />
                  </div>
                </section>
                <button
                  disabled={currentNode.type === "home"}
                  onClick={() => deleteNode(selectedId)}
                  className="btn-delete w-full py-3.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all shadow-sm"
                >
                  Elimina Pagina
                </button>
              </>
            )}
          </div>
        </aside>

        {!isSidePanelOpen && (
          <div className="absolute right-4 top-4 z-50 group">
            <button
              onClick={() => setIsSidePanelOpen(true)}
              className="p-2 bg-blue-600 text-white rounded-lg shadow-xl hover:scale-110 transition-transform"
            >
              <PanelRightOpen size={20} />
            </button>
            <span className="ttip ttip-l hide-on-export">pannello:apri</span>
          </div>
        )}
      </div>
    </div>
  );
}
